import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PipelineNodeData, getStatusIcon, getStatusColor } from '../types/pipeline.ts';

interface StepDetailModalProps {
  open: boolean;
  onClose: () => void;
  stepData: PipelineNodeData | null;
}

const StepDetailModal: React.FC<StepDetailModalProps> = ({ open, onClose, stepData }) => {
  if (!stepData) return null;

  // Debug logging
  if (open && stepData.step === 5) {
    console.log('[StepDetailModal] Opening modal for Step 5 with data:', {
      step: stepData.step,
      status: stepData.status,
      details: stepData.details,
      hasSeedOutputUrl: !!stepData.details?.seedOutputUrl,
      seedOutputUrl: stepData.details?.seedOutputUrl
    });
  }

  const statusColor = getStatusColor(stepData.status);
  const statusIcon = getStatusIcon(stepData.status);

  const renderStepDetails = () => {
    const details = stepData.details;

    // Debug: Log what data we have when modal opens
    console.log('[StepDetailModal] Opening modal for Step', stepData.step, 'with details:', details);

    switch (stepData.step) {
      case 1: // Select Images
        return (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Status:</strong> {stepData.status}
            </Typography>
            {details?.imageCount && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Images Selected:</strong> {details.imageCount}
              </Typography>
            )}
            {details?.s3Uri && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>S3 URI:</strong>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                    display: 'block',
                    wordBreak: 'break-all',
                  }}
                >
                  {details.s3Uri}
                </Typography>
              </Box>
            )}
            {stepData.message && (
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {stepData.message}
              </Typography>
            )}
            {details?.workspaceDirectoryUrl ? (
              <Box sx={{ mb: 1 }}>
                {console.log('[StepDetailModal] Step 1 - workspaceDirectoryUrl found:', details.workspaceDirectoryUrl)}
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Workspace Images:</strong>
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  href={details.workspaceDirectoryUrl}
                  target="_blank"
                  sx={{ mt: 0.5 }}
                >
                  View Workspace Images
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                    display: 'block',
                    wordBreak: 'break-all',
                    mt: 1,
                  }}
                >
                  {details.workspaceDirectoryUrl}
                </Typography>
              </Box>
            ) : (
              console.log('[StepDetailModal] Step 1 - workspaceDirectoryUrl NOT found. details:', details)
            )}
            {details?.cloudWatchUrl && (
              <Button
                variant="outlined"
                size="small"
                href={details.cloudWatchUrl}
                target="_blank"
                sx={{ mt: 1 }}
              >
                View CloudWatch Logs
              </Button>
            )}
          </Box>
        );

      case 2: // Download Images
        return (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Status:</strong> {stepData.status}
            </Typography>
            {details?.downloadPath && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Download Path:</strong> {details.downloadPath}
              </Typography>
            )}
            {details?.downloadedCount && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Files Downloaded:</strong> {details.downloadedCount}
              </Typography>
            )}
            {stepData.message && (
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {stepData.message}
              </Typography>
            )}
          </Box>
        );

      case 3: // Get Model Config
        return (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Status:</strong> {stepData.status}
            </Typography>
            {details?.modelName && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Model Name:</strong> {details.modelName}
              </Typography>
            )}
            {details?.classesNumber && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Number of Classes:</strong> {details.classesNumber}
              </Typography>
            )}
            {stepData.message && (
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {stepData.message}
              </Typography>
            )}
          </Box>
        );

      case 4: // Register Batch
        return (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Status:</strong> {stepData.status}
            </Typography>
            {details?.batchId && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Batch ID:</strong> {details.batchId}
              </Typography>
            )}
            {stepData.message && (
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {stepData.message}
              </Typography>
            )}
          </Box>
        );

      case 5: // Bulk Predict
        return (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Status:</strong> {stepData.status}
            </Typography>
            {details?.processedCount && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Images Processed:</strong> {details.processedCount}
              </Typography>
            )}
            {details?.batchId && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Batch ID:</strong> {details.batchId}
              </Typography>
            )}
            {details?.seedOutputUrl && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Seed Output URL:</strong>
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  href={details.seedOutputUrl}
                  target="_blank"
                  sx={{ mt: 0.5 }}
                >
                  View Seed Server Output
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                    display: 'block',
                    wordBreak: 'break-all',
                    mt: 1,
                  }}
                >
                  {details.seedOutputUrl}
                </Typography>
              </Box>
            )}
            {stepData.message && (
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {stepData.message}
              </Typography>
            )}
            {details?.sampleResponses && details.sampleResponses.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Sample Responses:</strong>
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {details.sampleResponses.slice(0, 5).map((response, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      sx={{ fontFamily: 'monospace', display: 'block' }}
                    >
                      {response}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        );

      case 6: // Add Annotations
        return (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Status:</strong> {stepData.status}
            </Typography>
            {details?.batchId && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Batch ID:</strong> {details.batchId}
              </Typography>
            )}
            {details?.annotationCount && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Annotations Added:</strong> {details.annotationCount}
              </Typography>
            )}
            {details?.insightsCount && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Insights Generated:</strong> {details.insightsCount}
              </Typography>
            )}
            {stepData.message && (
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {stepData.message}
              </Typography>
            )}
          </Box>
        );

      case 7: // Extract Weed Instances
        return (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Status:</strong> {stepData.status}
            </Typography>
            {details?.highWeedCount && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>High-Weed Images:</strong> {details.highWeedCount}
              </Typography>
            )}
            {details?.instancesExtracted && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Instances Extracted:</strong> {details.instancesExtracted}
              </Typography>
            )}
            {details?.weedExtractsUri && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Weed Extracts S3 URI:</strong>
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                    display: 'block',
                    wordBreak: 'break-all',
                  }}
                >
                  {details.weedExtractsUri}
                </Typography>
              </Box>
            )}
            {details?.workspaceDirectoryUrl ? (
              <Box sx={{ mb: 1 }}>
                {console.log('[StepDetailModal] Step 7 - workspaceDirectoryUrl found:', details.workspaceDirectoryUrl)}
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Workspace Weed Instances:</strong>
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  href={details.workspaceDirectoryUrl}
                  target="_blank"
                  sx={{ mt: 0.5 }}
                >
                  View Weed Instances
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: '#f5f5f5',
                    p: 1,
                    borderRadius: 1,
                    display: 'block',
                    wordBreak: 'break-all',
                    mt: 1,
                  }}
                >
                  {details.workspaceDirectoryUrl}
                </Typography>
              </Box>
            ) : (
              console.log('[StepDetailModal] Step 7 - workspaceDirectoryUrl NOT found. details:', details)
            )}
            {stepData.message && (
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {stepData.message}
              </Typography>
            )}
          </Box>
        );

      default:
        return (
          <Typography variant="body2">
            No additional details available for this step.
          </Typography>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 5,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: `3px solid ${statusColor}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ fontSize: '1.5rem' }}>{statusIcon}</span>
          <Typography variant="h6" component="span">
            Step {stepData.step}: {stepData.name}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.05)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {/* Status chip */}
        <Box sx={{ mb: 2 }}>
          <Chip
            label={stepData.status.toUpperCase()}
            sx={{
              bgcolor: statusColor,
              color: 'white',
              fontWeight: 600,
              fontSize: 12,
            }}
            size="small"
          />
          {stepData.duration && (
            <Chip
              label={`Duration: ${stepData.duration}s`}
              sx={{ ml: 1, fontSize: 12 }}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Step-specific details */}
        {renderStepDetails()}

        {/* Error message (if status is error) */}
        {stepData.status === 'error' && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: '#ffebee',
              borderRadius: 1,
              borderLeft: '4px solid #f44336',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Error Details:
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {stepData.message || 'An error occurred during this step.'}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StepDetailModal;
